import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Clock, FileText, Lock, Play, Sparkles } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr, inr } from "./portalApi";
import { useBuy } from "./PortalStore";
import "../test/testTheme.css";

function TestsInner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [starting, setStarting] = useState("");
  const { buy, buying, error: buyError, setBuying } = useBuy((order) => {
    setNotice(`Payment verified — "${order.item_name}" unlocked.`);
    setBuying("");
    load();
  });

  const load = useCallback(() => {
    portalApi.get("/portal/tests").then(({ data: d }) => setData(d)).catch(() => setError("Could not load tests. Please refresh."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (t) => {
    setStarting(t.test_id);
    setError("");
    try {
      await portalApi.post(`/portal/tests/${t.test_id}/start`);
      navigate(`/portal/tests/${t.test_id}`);
    } catch (err) {
      if (err?.response?.status === 402) setError(portalErr(err));
      else setError(portalErr(err));
    }
    setStarting("");
  };

  return (
    <div className="tp-wrap" data-testid="portal-tests">
      <div className="tp-hero">
        <span className="tp-eyebrow">FULL-LENGTH MOCK TESTS</span>
        <h1 className="tp-h1" style={{ fontSize: 32 }}>Real-exam practice tests</h1>
        <p className="tp-sub">45-question papers with a live countdown, question palette and full solutions — {inr(data?.price_paise ?? 2100)} per test, or free with the {inr(data?.pass_price_paise ?? 9900)}/month Test Pass ({data?.pass_free_per_topic ?? 3} free tests per topic every month).</p>
      </div>
      {notice && <p className="tp-success" data-testid="tests-notice">{notice}</p>}
      {(error || buyError) && <p className="tp-error" data-testid="tests-error">{error || buyError}</p>}
      {!data?.has_pass && <div className="tp-panel" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 16 }} data-testid="pass-upsell">
        <Sparkles size={22} style={{ color: "#f77f00" }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <b>Mock Test Pass — {inr(data?.pass_price_paise ?? 9900)}/month</b>
          <p className="tp-note" style={{ margin: "2px 0 0" }}>3 free full-length tests per topic, every month. Single tests cost {inr(data?.price_paise ?? 2100)} each.</p>
        </div>
        <button className="tp-btn small" disabled={!!buying} onClick={() => buy("product", "test_pass_monthly", "Mock Test Pass (Monthly)")} data-testid="buy-pass-btn">
          {buying === "test_pass_monthly" ? "Opening payment…" : "Get the pass"}
        </button>
      </div>}
      {data?.has_pass && <p className="tp-success" data-testid="pass-active"><BadgeCheck size={15} style={{ verticalAlign: -2 }} /> Your Mock Test Pass is active this month.</p>}

      {!data && <p className="tp-muted">Loading tests…</p>}
      {data?.tests?.length === 0 && <div className="tp-panel" data-testid="tests-empty"><p className="tp-muted">No mock tests published yet — check back soon.</p></div>}
      {data?.tests?.map((t, i) => {
        const access = t.access.access;
        return (
          <div className="tp-panel" key={t.test_id} data-testid={`test-card-${i}`} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <FileText size={22} style={{ color: "#f77f00", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <b data-testid={`test-name-${i}`}>{t.name}</b>
              <p className="tp-note" style={{ margin: "2px 0 0" }}>{t.topic_name} · {t.total} questions · <Clock size={11} style={{ verticalAlign: -1 }} /> {t.duration_minutes} min{t.my_attempts ? ` · attempted ${t.my_attempts}×` : ""}</p>
              {access === "pass" && <p className="tp-note" style={{ color: "#1a7f4b", margin: "2px 0 0" }}>Free with your pass — {t.access.free_uses_left} of {data.pass_free_per_topic} left this month for this topic</p>}
            </div>
            {access === "owned" && <span className="tp-pill" style={{ background: "#e7f8f0", color: "#1a7f4b" }} data-testid={`test-owned-${i}`}>PURCHASED</span>}
            {t.my_attempts > 0 && <button className="tp-btn ghost small" onClick={() => navigate(`/portal/tests/${t.test_id}/result`)} data-testid={`test-result-${i}`}>Result</button>}
            {access === "pay"
              ? <button className="tp-btn small" disabled={!!buying} onClick={() => buy("mock_test", t.test_id, t.name)} data-testid={`test-buy-${i}`}><Lock size={13} /> {buying === t.test_id ? "Opening payment…" : `Buy ${inr(t.price_paise ?? data.price_paise)}`}</button>
              : <button className="tp-btn small" disabled={!!starting} onClick={() => start(t)} data-testid={`test-start-${i}`}><Play size={13} /> {starting === t.test_id ? "Starting…" : t.my_attempts ? "Re-attempt" : "Start test"}</button>}
          </div>
        );
      })}
    </div>
  );
}

export default function PortalTests() {
  return <PortalShell><TestsInner /></PortalShell>;
}
